import { Component, type ErrorInfo, type ReactNode } from "react"
import { captureException } from "@/lib/sentry"
import { AlertTriangle, RefreshCw, ChevronDown, ChevronUp } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"

interface ErrorBoundaryProps {
  children: ReactNode
  /** Fallback UI to render instead of the default error panel */
  fallback?: ReactNode
  /** Called when an error is caught - use for logging/reporting */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  /** Label shown in the error panel (e.g. "Document Viewer", "Search") */
  label?: string
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  showDetails: boolean
}

/**
 * React Error Boundary - catches JS errors in child component tree.
 *
 * Usage:
 *   <ErrorBoundary label="Document Viewer">
 *     <DocumentViewer />
 *   </ErrorBoundary>
 *
 * Without this, any uncaught error in a child component crashes the
 * entire React app (white screen). With it, only the affected section
 * shows an error panel while the rest of the app keeps working.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, showDetails: false }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console in dev, report to Sentry in prod
    console.error(`[ErrorBoundary${this.props.label ? `: ${this.props.label}` : ""}]`, error, errorInfo)
    captureException(error, {
      componentStack: errorInfo.componentStack ?? undefined,
      label: this.props.label,
    })
    this.props.onError?.(error, errorInfo)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, showDetails: false })
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    if (this.props.fallback) {
      return this.props.fallback
    }

    const { error, showDetails } = this.state
    const label = this.props.label || "this section"

    return (
      <div className="flex items-center justify-center p-6" role="alert">
        <Alert variant="destructive" className="max-w-lg shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 forensic-grid opacity-[0.03]" />
          <AlertTriangle className="h-4 w-4 relative z-10" />
          <AlertTitle className="relative z-10">Something went wrong in {label.toUpperCase()}</AlertTitle>
          <AlertDescription className="mt-2 space-y-4 relative z-10">
            <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground/80 leading-relaxed">
              Something went wrong. Your data is safe - try refreshing the page.
              Your data is safe. Try refreshing this section, or reload the page if that doesn't work.
            </p>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={this.handleRetry}
                aria-label={`Retry loading ${label}`}
                className="font-mono text-xs uppercase tracking-wide h-8 rounded-lg"
              >
                <RefreshCw className="mr-1.5 h-3 w-3" />
                Retry
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.location.reload()}
                aria-label="Reload entire page"
                className="font-mono text-xs uppercase tracking-wide h-8 rounded-lg"
              >
                Reload Page
              </Button>
            </div>

            {error && (
              <button
                className="flex items-center gap-1 text-xs font-mono font-bold text-muted-foreground/60 hover:text-foreground transition-colors uppercase tracking-wide"
                onClick={() => this.setState({ showDetails: !showDetails })}
                aria-expanded={showDetails}
              >
                {showDetails ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
                Error Details
              </button>
            )}

            {showDetails && error && (
              <pre className="mt-1 max-h-32 overflow-auto rounded-lg bg-black/40 p-3 text-xs font-mono whitespace-pre-wrap text-destructive/70 border border-destructive/20">
                {error.message}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            )}
          </AlertDescription>
        </Alert>
      </div>
    )
  }
}

export default ErrorBoundary
